import React from "react";
import BookingButton from "../components/BookingButton";

/**
 * TryPage: Entry point for users to start using the service
 * Offers two options: consultation booking or DIY tool
 */
const TryPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section-padding gradient-hero">
        <div className="container-max text-center">
          <div className="animate-fade-in">
            <h1 className="heading-1 mb-6">Try ReadyToClaim</h1>
            <p className="body-large max-w-3xl mx-auto">
              Choose how you'd like to get started with our inventory system
            </p>
          </div>
        </div>
      </section>

      {/* CTA Cards */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Consultation Card */}
            <div className="card p-8 hover-lift animate-fade-in">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <h2 className="heading-3 mb-4">Schedule a Consultation</h2>
                <p className="body-medium mb-6">Book a 30-60 minute Zoom video call with our specialist. We'll record the session, document your entire home inventory, and use reverse image search to create efficient, professional documentation.</p>
                <BookingButton 
                  variant="primary" 
                  size="lg"
                  className="btn-primary"
                >
                  Book Consultation
                </BookingButton>
                <p className="text-sm text-gray-500 mt-4">Schedule a video call with our specialist</p>
              </div>
            </div>

            {/* DIY Tool Card */}
            <div className="card p-8 hover-lift animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <h2 className="heading-3 mb-4">Try the Free DIY Tool</h2>
                <p className="body-medium mb-6">Start documenting your belongings right now with our inventory system. Upload photos and get instant valuations.</p>
                <a href="https://app.readytoclaim.com" className="btn-primary inline-block bg-green-600 hover:bg-green-700">Start Free Trial</a>
                <p className="text-sm text-gray-500 mt-4">*Link to the inventory system</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">
              How It Works
            </h2>
            <p className="body-large max-w-3xl mx-auto">
              Our simple 3-step process makes documenting your belongings effortless
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center animate-fade-in">
              <div className="w-20 h-20 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">1. Video Call</h3>
              <p className="body-medium">Schedule a 30-60 minute Zoom call with our specialist. We'll record the entire session as we walk through your home together.</p>
            </div>

            <div className="text-center animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="w-20 h-20 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">2. Documentation</h3>
              <p className="body-medium">Our specialist documents every item from the recording, creating a comprehensive inventory of your belongings with detailed descriptions.</p>
            </div>

            <div className="text-center animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="w-20 h-20 bg-primary-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">3. Reverse Image Search</h3>
              <p className="body-medium">We use reverse image search technology to analyze the documentation, provide accurate valuations, and create professional insurance-ready reports.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding gradient-primary">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="card p-8 animate-fade-in">
              <h3 className="heading-3 mb-4">How does the consultation process work?</h3>
              <p className="body-medium">We schedule a 30-60 minute Zoom video call with our specialist. During the call, we record the entire session as we walk through your home together. The specialist documents every item, and then we use reverse image search to process the information for accurate valuations and professional reports.</p>
            </div>



            <div className="card p-8 animate-fade-in" style={{animationDelay: '0.2s'}}>
              <h3 className="heading-3 mb-4">How long does the entire process take?</h3>
              <p className="body-medium">The video call takes 30-60 minutes, depending on the size of your home. After the call, our specialist typically completes the documentation and reverse image search processing within 24-48 hours, delivering your final report shortly after.</p>
            </div>

            <div className="card p-8 animate-fade-in" style={{animationDelay: '0.3s'}}>
              <h3 className="heading-3 mb-4">Is the video call recorded?</h3>
              <p className="body-medium">Yes, we record the entire Zoom session with your consent. This recording helps our specialist create accurate documentation and ensures we don't miss any details about your belongings during the inventory process.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TryPage; 